import {
  AllowedOAuthFlow,
  audienceUri,
  AuthClientItem,
  darkThemeStyles,
  JupiterTheme,
  urls,
} from '@jupiterone/jupiter-web-toolkit';
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
} from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { ChangeEvent } from 'react';
import { Lock } from 'react-feather';
import AccountPageTextField from 'navbar/src/jupiterAccountSettings/AccountSettings/AccountPageList/AccountPageTextField';
import EditHeader from 'navbar/src/jupiterAccountSettings/AccountSettings/components/EditHeader';

const styles = (theme: JupiterTheme) =>
  createStyles({
    ...darkThemeStyles,
    ssoHelpText: {
      color: 'white',
    },
    oAuthFlowFormGroup: {
      marginTop: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
    },
    oAuthFlowFormLabel: {
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(2),
    },
    formCheckboxItem: {
      marginBottom: theme.spacing(-1),
    },
  });

interface Props {
  classes: any;
  authClientState: [AuthClientItem, (authClient: AuthClientItem) => void];
  onDelete: () => void;
  onSave: () => void;
  onBack: () => void;
}

const AuthClientDetail = ({
  classes,
  authClientState,
  onBack,
  onDelete,
  onSave,
}: Props) => {
  const [authClient, setAuthClient] = authClientState;

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAuthClient({ ...authClient, name: e.target.value });
  };

  const handleMetadataUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
    const customIdentityProvider = {
      ...authClient.customIdentityProvider,
      metadataUrl: e.target.value,
    };
    setAuthClient({ ...authClient, customIdentityProvider });
  };

  const handleMetadataFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const customIdentityProvider = {
      ...authClient.customIdentityProvider,
      metadataFile: e.target.value,
    };
    setAuthClient({ ...authClient, customIdentityProvider });
  };

  const handleChangeCheckbox = (allowedAuthFlow: AllowedOAuthFlow) => (
    event: any
  ) => {
    const isChecked = event.target.checked;
    // add allowed auth flow
    if (isChecked) {
      const allowedOAuthFlows = [
        ...authClient.allowedOAuthFlows,
        allowedAuthFlow,
      ];
      setAuthClient({ ...authClient, allowedOAuthFlows });
    }
    // remove allowed auth flow
    else {
      const allowedOAuthFlows = authClient.allowedOAuthFlows.filter(
        (authFlow) => authFlow !== allowedAuthFlow
      );
      setAuthClient({ ...authClient, allowedOAuthFlows });
    }
  };

  const { allowedOAuthFlows, customIdentityProvider } = authClient;

  const metadataUrl =
    customIdentityProvider && customIdentityProvider.metadataUrl;

  const metadataFile =
    customIdentityProvider && customIdentityProvider.metadataFile;

  const codeDisabled = allowedOAuthFlows.includes(
    AllowedOAuthFlow.CLIENT_CREDENTIALS
  );
  const implicitDisabled = allowedOAuthFlows.includes(
    AllowedOAuthFlow.CLIENT_CREDENTIALS
  );
  const clientCredentialsDisabled =
    allowedOAuthFlows.includes(AllowedOAuthFlow.CODE) ||
    allowedOAuthFlows.includes(AllowedOAuthFlow.IMPLICIT);

  return (
    <EditHeader
      handleBackButton={onBack}
      titleText={authClient.name}
      titleIcon={<Lock />}
      onDeleteItem={onDelete}
      onSaveItem={onSave}
    >
      <form>
        <AccountPageTextField
          disabled={true}
          label="SSO URL"
          helperText="This is the single sign-on URL that you need to enter in your IdP SSO application configuration Also use the above for Recipient URL and Destination URL"
          value={urls.ssoUrl}
        />
        <AccountPageTextField
          disabled={true}
          label="Audience URI (SP Entity ID)"
          helperText="This is the single sign-on audience URI that you need to enter in your IdP SSO application configuration"
          value={audienceUri}
        />
        <AccountPageTextField
          label="Client name"
          value={authClient.name}
          onChange={handleNameChange}
        />
        <AccountPageTextField
          label="SAML Metadata Document URL"
          value={metadataUrl}
          onChange={handleMetadataUrlChange}
        />
        <AccountPageTextField
          label="SAML Metadata File"
          value={metadataFile}
          onChange={handleMetadataFileChange}
        />
        <FormGroup row className={classes.oAuthFlowFormGroup}>
          <FormLabel
            className={clsx(classes.formLabel, classes.oAuthFlowFormLabel)}
          >
            Allowed OAuth Flows
          </FormLabel>
          <FormControlLabel
            className={classes.formCheckboxItem}
            classes={{
              label: classes.checkboxLabel,
              disabled: classes.checkboxLabelDisabled,
            }}
            control={
              <Checkbox
                color="primary"
                checked={allowedOAuthFlows.includes(AllowedOAuthFlow.CODE)}
                disabled={codeDisabled}
                onChange={handleChangeCheckbox(AllowedOAuthFlow.CODE)}
                classes={{
                  disabled: classes.checkboxDisabled,
                  colorSecondary: classes.checkboxSecondary,
                }}
              />
            }
            label="Authorization code grant"
          />
          <FormControlLabel
            className={classes.formCheckboxItem}
            classes={{
              label: classes.checkboxLabel,
              disabled: classes.checkboxLabelDisabled,
            }}
            control={
              <Checkbox
                color="primary"
                checked={allowedOAuthFlows.includes(AllowedOAuthFlow.IMPLICIT)}
                disabled={implicitDisabled}
                onChange={handleChangeCheckbox(AllowedOAuthFlow.IMPLICIT)}
                classes={{
                  disabled: classes.checkboxDisabled,
                  colorSecondary: classes.checkboxSecondary,
                }}
              />
            }
            label="Implicit Grant"
          />
          <FormControlLabel
            className={classes.formCheckboxItem}
            classes={{
              label: classes.checkboxLabel,
              disabled: classes.checkboxLabelDisabled,
            }}
            control={
              <Checkbox
                color="primary"
                checked={allowedOAuthFlows.includes(
                  AllowedOAuthFlow.CLIENT_CREDENTIALS
                )}
                disabled={clientCredentialsDisabled}
                onChange={handleChangeCheckbox(
                  AllowedOAuthFlow.CLIENT_CREDENTIALS
                )}
                classes={{
                  disabled: classes.checkboxDisabled,
                  colorSecondary: classes.checkboxSecondary,
                }}
              />
            }
            label="Client credentials"
          />
        </FormGroup>
      </form>
    </EditHeader>
  );
};

export default withStyles(styles)(AuthClientDetail);
export { AuthClientDetail as AuthClientDetailUnwrapped };
